import React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextField, Box, Checkbox, FormControlLabel } from "@mui/material";
import FormDialog from "@components/FormDialog";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "@context/SnackBarProvider";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { DriverResponse } from "@lib/types/driver";
import useHandleError from "@lib/utils/errorHook";

import { createUser, UserFormData, userSchema } from "@lib/api/mutations/users";

interface Props {
  createUserDialogOpen: boolean;
  setCreateUserDialogOpen: (open: boolean) => void;
  refetch?: () => void;
}

const CreateUser: React.FC<Props> = ({
  createUserDialogOpen,
  setCreateUserDialogOpen,
  refetch,
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      organization: "",
    },
  });

  const { mutate, isPending: isCreatingUser } = useMutation({
    mutationFn: createUser,
    onSuccess: (data: DriverResponse) => {
      showSnackbar(t("admin.users.addSuccess"), "success");

      setCreateUserDialogOpen(false);
      reset();
      refetch && refetch();
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && showSnackbar(errorDetails.message, "error");
      } else {
        showSnackbar(t("general.error"), "error");
      }
    },
  });

  const handleCreateUser = (data: UserFormData) => {
    mutate(data);
  };

  return (
    <FormDialog
      title={t("admin.users.add")}
      open={createUserDialogOpen}
      onClose={() => {
        setCreateUserDialogOpen(false);
        reset();
      }}
      onSubmit={handleSubmit(handleCreateUser)}
      submitText={t("general.save")}
      isSubmitting={isCreatingUser}
      submittingText={t("general.saving")}
    >
      <form noValidate>
        <Box display="flex" flexDirection="column" gap={2} py={2}>
          <Controller
            name="first_name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("admin.users.firstName")}
                variant="outlined"
                fullWidth
                error={!!errors.first_name}
                helperText={errors.first_name?.message}
              />
            )}
          />
          <Controller
            name="last_name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("admin.users.lastName")}
                variant="outlined"
                fullWidth
                error={!!errors.last_name}
                helperText={errors.last_name?.message}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("admin.users.email")}
                variant="outlined"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />

          <Controller
            name="organization"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("admin.users.organization")}
                variant="outlined"
                fullWidth
                error={!!errors.organization}
                helperText={errors.organization?.message}
              />
            )}
          />
        </Box>
      </form>
    </FormDialog>
  );
};

export default CreateUser;
